/**
 * GA4 Analytics Event Tracking Utility for ExpertMD
 *
 * Lightweight wrapper around gtag for type-safe event tracking.
 * All functions no-op gracefully if gtag is not loaded.
 *
 * NOTE: This module does NOT load the GA4 script tag. That must be
 * done separately (e.g., in index.html) once a measurement ID is available.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// ---------------------------------------------------------------------------
// Core event dispatcher
// ---------------------------------------------------------------------------

/**
 * Send a custom event to GA4 via gtag. No-ops if gtag is not available.
 *
 * @param eventName  - The GA4 event name (snake_case recommended by Google).
 * @param params     - Optional key/value parameters attached to the event.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', eventName, params);
}

// ---------------------------------------------------------------------------
// Virtual pageview tracking (SPA)
// ---------------------------------------------------------------------------

/**
 * Update the browser URL and send a pageview to GA4.
 *
 * Uses history.pushState so the URL bar reflects the current screen
 * (e.g. /auth/signup, /dashboard) without a full page reload. This
 * gives Google Ads a real page path to attach conversions to.
 *
 * Skips pushState when the URL already matches (initial page load).
 *
 * @param pagePath  - Virtual path, e.g. "/dashboard" or "/auth/signup".
 * @param pageTitle - Optional title; falls back to document.title.
 */
const GA_MEASUREMENT_ID = 'G-DX8Y2WTYPQ';

export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (typeof window === 'undefined') return;

  // Update browser URL if it doesn't already match
  if (window.location.pathname !== pagePath) {
    window.history.pushState(null, '', pagePath);
  }

  // Send pageview to GA4
  if (typeof window.gtag === 'function') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  }
}

// ---------------------------------------------------------------------------
// Key conversion events
// ---------------------------------------------------------------------------

/** Fires the Google Ads conversion event for sign-up. */
export function trackSignUpConversion(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', 'conversion', {
    send_to: 'AW-18003828858/RZnmCILx7IUcEPrA8ohD',
  });
}

/** Fires when a user completes signup. */
export function trackSignUp(): void {
  trackEvent('sign_up', { method: 'email' });
}

/** Fires when a user requests a demo. */
export function trackDemoRequest(): void {
  trackEvent('demo_request');
}

/** Fires when a user views the pricing section. */
export function trackPricingView(): void {
  trackEvent('pricing_view');
}

/** Fires when a user visits the /compare page. */
export function trackComparePageView(): void {
  trackEvent('compare_page_view');
}

/**
 * Fires on any landing page view (e.g. /physicians, /compare, /side-income).
 *
 * @param page - The landing page path (e.g. "/physicians").
 */
export function trackLandingPageView(page: string): void {
  trackEvent('landing_page_view', { page });
}

// ---------------------------------------------------------------------------
// Micro-conversion events
// ---------------------------------------------------------------------------

/**
 * Fires on any CTA button click.
 *
 * @param ctaName  - Human-readable name of the CTA (e.g. "hero_get_started").
 * @param location - Where on the page the CTA appears (e.g. "hero", "footer").
 */
export function trackCTAClick(ctaName: string, location: string): void {
  trackEvent('cta_click', { cta_name: ctaName, location });
}

/**
 * Fires at scroll depth milestones (25%, 50%, 75%, 100%).
 *
 * @param percentage - The scroll depth percentage reached.
 */
export function trackScrollDepth(percentage: number): void {
  trackEvent('scroll_depth', { percentage });
}

/**
 * Fires when a blog post is read.
 *
 * @param postSlug - The URL slug of the blog post.
 */
export function trackBlogPostView(postSlug: string): void {
  trackEvent('blog_post_view', { post_slug: postSlug });
}

/**
 * Fires when an FAQ item is expanded.
 *
 * @param question - The text of the FAQ question that was expanded.
 */
export function trackFAQExpand(question: string): void {
  trackEvent('faq_expand', { question });
}

// ---------------------------------------------------------------------------
// UTM parameter utilities
// ---------------------------------------------------------------------------

/**
 * Parse UTM parameters from the current URL's query string.
 *
 * @returns An object containing only the UTM params that are present.
 *          Keys are the raw param names (e.g. "utm_source").
 */
export function getUTMParams(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

  const result: Record<string, string> = {};

  for (const key of utmKeys) {
    const value = searchParams.get(key);
    if (value) {
      result[key] = value;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// UTM naming convention constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Intake form events
// ---------------------------------------------------------------------------

/** Fires when the attorney intake form loads. */
export function trackIntakeFormStart(vanityUrl: string): void {
  trackEvent('intake_form_start', { vanity_url: vanityUrl });
}

/** Fires on each step progression in the intake form. */
export function trackIntakeFormStep(step: number, stepName: string): void {
  trackEvent('intake_form_step', { step, step_name: stepName });
}

/** Fires on successful intake form submission. */
export function trackIntakeFormSubmit(vanityUrl: string): void {
  trackEvent('intake_form_submit', { vanity_url: vanityUrl });
}

/** Fires on intake form validation error. */
export function trackIntakeFormError(step: number, error: string): void {
  trackEvent('intake_form_error', { step, error });
}

// ---------------------------------------------------------------------------
// Post-signup activation events
// ---------------------------------------------------------------------------

/** Fires when a doctor completes their profile setup. */
export function trackProfileComplete(): void {
  trackEvent('profile_complete');
}

/** Fires when a doctor initiates Stripe Connect onboarding. */
export function trackStripeConnectStart(): void {
  trackEvent('stripe_connect_start');
}

/** Fires when a doctor returns from Stripe with a success status. */
export function trackStripeConnectComplete(): void {
  trackEvent('stripe_connect_complete');
}

/** Fires when a doctor enables E2E encryption. */
export function trackEncryptionSetup(): void {
  trackEvent('encryption_setup');
}

/** Fires when a doctor accepts a pre-screening request. */
export function trackCaseAccept(): void {
  trackEvent('case_accept');
}

// ---------------------------------------------------------------------------
// Feature engagement events
// ---------------------------------------------------------------------------

/** Fires when a new case is created. */
export function trackCaseCreate(source: 'import' | 'intake'): void {
  trackEvent('case_create', { source });
}

/** Fires when an invoice is created. */
export function trackInvoiceCreate(): void {
  trackEvent('invoice_create');
}

/** Fires when an appointment is scheduled. */
export function trackAppointmentSchedule(): void {
  trackEvent('appointment_schedule');
}

// ---------------------------------------------------------------------------
// UTM naming convention constants
// ---------------------------------------------------------------------------

export const UTM_CAMPAIGNS = {
  EXPERT_WITNESS_INTENT: 'expert-witness-intent',
  COMPETITOR_CONQUEST: 'competitor-conquest',
  PHYSICIAN_SIDE_INCOME: 'physician-side-income',
} as const;
